import type { PartitionDoorConfig, RoomConfig } from '@/model/schema';
import type { Rng } from './rng';

/**
 * Builds the interior-door layout for one story.
 *
 * Core idea: every room must be reachable from every other (via interior
 * doors only — no walking through walls), but it would feel unrealistic if
 * EVERY interior wall had a door. So we:
 *
 *   1. Discover the room adjacency graph: two rooms are adjacent if they
 *      share a continuous interior wall segment long enough to fit a door.
 *   2. Compute a spanning tree of that graph using a randomized BFS from
 *      a "hall" room when possible (so the hall becomes a circulation hub).
 *   3. Add a small number of extra doors (circulation loops) on top of the
 *      tree, so multi-room stories don't feel strictly tree-shaped.
 *
 * The door is centered on the shared wall segment.
 *
 * Determinism: the RNG is forked off the story RNG so different stories
 * make independent layout decisions, but a given seed always produces the
 * same plan.
 */
export function planInteriorDoors(rng: Rng, rooms: RoomConfig[]): PartitionDoorConfig[] {
  if (rooms.length <= 1) return [];

  const edges = enumerateAdjacencies(rooms);
  if (edges.length === 0) return [];

  const adjacency = new Map<number, Edge[]>();
  for (const e of edges) {
    if (!adjacency.has(e.a)) adjacency.set(e.a, []);
    if (!adjacency.has(e.b)) adjacency.set(e.b, []);
    adjacency.get(e.a)!.push(e);
    adjacency.get(e.b)!.push(e);
  }

  // BFS spanning tree, preferring a hall as the root. Ties broken by RNG.
  const halls = rooms
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => r.type === 'hall');
  const rootIndex =
    halls.length > 0
      ? halls[Math.floor(rng.next() * halls.length)]!.i
      : Math.floor(rng.next() * rooms.length);

  const visited = new Set<number>([rootIndex]);
  const queue: number[] = [rootIndex];
  const treeEdges: Edge[] = [];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    const neighbours = rng.shuffle(adjacency.get(cur) ?? []);
    for (const e of neighbours) {
      const other = e.a === cur ? e.b : e.a;
      if (visited.has(other)) continue;
      visited.add(other);
      treeEdges.push(e);
      queue.push(other);
    }
  }

  // If the graph is disconnected (e.g. rooms tucked behind a wider room
  // with no doorable shared wall), do nothing for the unreachable rooms —
  // we'd rather render a broken partition than a fake door. The scorer
  // should penalise such floor plans separately.

  // Add a few extra circulation doors at random, capped at ~1/3 of the
  // remaining (non-tree) edges so the result still looks tree-like.
  const treeKey = new Set(treeEdges.map(edgeKey));
  const remaining = rng.shuffle(edges.filter((e) => !treeKey.has(edgeKey(e))));
  const extras = Math.floor(remaining.length / 3);
  for (let i = 0; i < extras && i < remaining.length; i++) {
    treeEdges.push(remaining[i]!);
  }

  return treeEdges.map((e) => ({
    ownerRoomId: e.ownerRoomId,
    axis: e.axis,
    tCenter: e.tCenter,
    width: 0.9,
    height: 2.05,
  }));
}

interface Edge {
  /** Indices into the rooms array. */
  a: number;
  b: number;
  /** Which side of the +X / +Z partition rule the wall sits on. */
  ownerRoomId: string;
  axis: 'X' | 'Z';
  /** Position of the door center along the shared wall, normalized 0..1
   *  along the partition's owning room's wall (matching what the renderer
   *  uses to position the doorway). */
  tCenter: number;
}

const EPS = 1e-3;

function enumerateAdjacencies(rooms: RoomConfig[]): Edge[] {
  const out: Edge[] = [];
  for (let i = 0; i < rooms.length; i++) {
    for (let j = i + 1; j < rooms.length; j++) {
      const a = rooms[i]!;
      const b = rooms[j]!;

      // +X partition: a's right edge touches b's left edge (or vice versa).
      const xCase = sharedZRange(a, b);
      if (xCase) {
        const { ownerRoomId, axis, owner, sharedLo, sharedHi } = xCase;
        const sharedLen = sharedHi - sharedLo;
        if (sharedLen >= 1.4) {
          // Convert shared-segment center back to a t along the owner wall
          // (the owner's full wall span is owner.z..owner.z+owner.depth for
          // an +X partition; full owner.x..owner.x+owner.width for +Z).
          const ownerStart = axis === 'X' ? owner.z : owner.x;
          const ownerLen = axis === 'X' ? owner.depth : owner.width;
          const centerAbs = (sharedLo + sharedHi) / 2;
          const t = (centerAbs - ownerStart) / ownerLen;
          out.push({
            a: i,
            b: j,
            ownerRoomId,
            axis,
            tCenter: clamp(t, 0.05, 0.95),
          });
        }
      }
    }
  }
  return out;
}

function sharedZRange(
  a: RoomConfig,
  b: RoomConfig,
):
  | { ownerRoomId: string; axis: 'X' | 'Z'; owner: RoomConfig; sharedLo: number; sharedHi: number }
  | null {
  // +X partition (vertical wall along Z).
  if (Math.abs(a.x + a.width - b.x) < EPS) {
    const lo = Math.max(a.z, b.z);
    const hi = Math.min(a.z + a.depth, b.z + b.depth);
    if (hi - lo > EPS) {
      return { ownerRoomId: a.id, axis: 'X', owner: a, sharedLo: lo, sharedHi: hi };
    }
  }
  if (Math.abs(b.x + b.width - a.x) < EPS) {
    const lo = Math.max(a.z, b.z);
    const hi = Math.min(a.z + a.depth, b.z + b.depth);
    if (hi - lo > EPS) {
      return { ownerRoomId: b.id, axis: 'X', owner: b, sharedLo: lo, sharedHi: hi };
    }
  }
  // +Z partition (horizontal wall along X).
  if (Math.abs(a.z + a.depth - b.z) < EPS) {
    const lo = Math.max(a.x, b.x);
    const hi = Math.min(a.x + a.width, b.x + b.width);
    if (hi - lo > EPS) {
      return { ownerRoomId: a.id, axis: 'Z', owner: a, sharedLo: lo, sharedHi: hi };
    }
  }
  if (Math.abs(b.z + b.depth - a.z) < EPS) {
    const lo = Math.max(a.x, b.x);
    const hi = Math.min(a.x + a.width, b.x + b.width);
    if (hi - lo > EPS) {
      return { ownerRoomId: b.id, axis: 'Z', owner: b, sharedLo: lo, sharedHi: hi };
    }
  }
  return null;
}

function edgeKey(e: Edge): string {
  return `${e.ownerRoomId}|${e.axis}|${e.tCenter.toFixed(3)}`;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
